import { useState } from "react"
import { Component, FieldErrorsObj, ParsedActionError } from "@/types"
import { cx } from "@/utils/cx"
import { SUPPORT_EMAIL_MAIL_TO } from "@/constants"
import { executeCaptcha } from "@/actions/captcha/executeCaptcha"
import { forgotPassword } from "@/actions/_archive/auth/forgotPassword"
import {
  validateForgotPassword,
  ForgotPasswordKeys,
} from "@/actions/_archive/auth/forgotPassword/forgotPassword.interface"

import { Button } from "@/ui/atoms/controls/Button"
import { Form } from "@/ui/atoms/form/Form"
import { TextLink } from "@/ui/atoms/controls/TextLink"
import { FieldGroup } from "@/ui/atoms/form/FieldGroup"
import { FormFields } from "@/ui/atoms/form/FormFields"
import { FormLinksWrapper } from "@/ui/atoms/form/FormLinksWrapper"
import { TextField } from "@/ui/atoms/form/TextField"
import { SplashCard } from "@/ui/atoms/layout/SplashCard"
import { Stack } from "@/ui/atoms/layout/Stack"
import { Title } from "@/ui/atoms/type/Title"
import { NotificationCard } from "@/ui/blocks/notifications/NotificationCard"
import { BodyText } from "@/ui/atoms/type/BodyText"

import { errors } from "./ForgotPasswordForm.errors"
import { useCourseStore } from "@/store/courseStoreProvider"
import { Pill } from "@/ui/atoms/data/Pill"

export const COMPONENT_NAME = "forgotPasswordForm"

export const presets = {}

export interface Props extends Component {
  onSuccess: (email: string) => void
}

export function ForgotPasswordForm({ onSuccess, className, testId = COMPONENT_NAME }: Props) {
  const { CAPTCHA_SITE_KEY } = useCourseStore((s) => s.envVars)

  const [email, setEmail] = useState("")

  const [formStatus, setFormStatus] = useState<"ready" | "submitting">("ready")
  const [formError, setFormError] = useState<null | ParsedActionError>(null)
  const [fieldErrors, setFieldErrors] = useState<null | FieldErrorsObj<ForgotPasswordKeys>>(null)

  async function handleSubmit() {
    setFieldErrors(null)
    setFormStatus("submitting")

    const [recaptchaResponse, recaptchaError] = await executeCaptcha(
      "forgotPassword",
      CAPTCHA_SITE_KEY,
    )
    if (recaptchaError) {
      setFormError(errors["CAPTCHA_FAILED"])
      setFormStatus("ready")
      return
    }

    const [validatedData, validationFieldErrors] = validateForgotPassword({
      email,
      recaptchaResponse,
    })
    if (validationFieldErrors) {
      setFormError(errors["INVALID_INPUT"])
      setFieldErrors(validationFieldErrors)
      setFormStatus("ready")
      return
    }

    const [_, forgotPasswordErrorCode] = await forgotPassword(validatedData)
    if (forgotPasswordErrorCode) {
      setFormError(errors[forgotPasswordErrorCode])
      setFormStatus("ready")
      return
    }
    onSuccess(email)
    setFormStatus("ready")
  }

  return (
    <SplashCard className={cx(`w-full max-w-screen-form`, className)} testId={testId}>
      <Form name="Forgot password" onSubmit={handleSubmit}>
        <header>
          <Stack className="gap-10 md:gap-8">
            <div className="flex justify-center items-center gap-2">
              <Title size="lg" className="text-center">
                Trouble signing in?
              </Title>
              <Pill>Legacy</Pill>
            </div>
            <NotificationCard
              variant="warning"
              title="Not for SANS account users"
              text="Most users should now sign in via SANS. Signing in with your email and password will only work for certain legacy accounts created before September 25, 2025."
              links={[
                {
                  label: "Sign in with SANS",
                  href: "/sign-in",
                },
              ]}
              className="border border-border-form border-l-0"
            />
            <BodyText size="md" className="text-center">
              Forgotten your password or not received your verification email? Enter your email
              below and we'll send you an email with a link to access your account.
            </BodyText>
          </Stack>
        </header>
        <main className="flex flex-col gap-8">
          <FormFields>
            <FieldGroup>
              <TextField
                name="email"
                error={fieldErrors?.email?.[0]}
                type="email"
                label="Email"
                testId="emailField"
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
              />
            </FieldGroup>
          </FormFields>

          {formError && (
            <Stack className="gap-3">
              <NotificationCard
                variant="failure"
                title={formError.title}
                text={formError.message}
                links={formError.links}
                className="border border-border-form border-l-0"
                testId="formErrorCard"
              />
            </Stack>
          )}
          <Button
            variant="primary"
            label="Submit email"
            width="full"
            type="submit"
            testId="submitButton"
            state={formStatus === "submitting" ? "loading" : "ready"}
          />
        </main>
        <footer className="">
          <FormLinksWrapper>
            <TextLink href="/legacy/sign-in" testId="signInLink" style="bold">
            Sign in with email and password (legacy)
            </TextLink>

            <TextLink href={SUPPORT_EMAIL_MAIL_TO} testId="contactUsLink" style="bold">
              Contact us
            </TextLink>
          </FormLinksWrapper>
        </footer>
      </Form>
    </SplashCard>
  )
}
